import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  LayoutDashboard, Calendar, ListChecks, Settings, LogOut,
  RefreshCw, CheckCircle, XCircle, AlertCircle, Clock,
  User, Mail, Phone, Building, MessageSquare, ChevronDown, ChevronUp,
  TrendingUp, Video
} from 'lucide-react';
import { toast } from 'sonner';
import AnimatedLogo from '../components/AnimatedLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── helpers ──────────────────────────────────────────────────────────────────
const todayStr = () => new Date().toISOString().split('T')[0];

const weekRange = () => {
  const now = new Date();
  const day = now.getDay();
  const mon = new Date(now); mon.setDate(now.getDate() - ((day + 6) % 7));
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return [mon.toISOString().split('T')[0], sun.toISOString().split('T')[0]];
};

const fmtDate = (str) =>
  str ? new Date(str).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// ─── sub-components ───────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = {
    confirmed: { bg: '#dcfce7', color: '#16a34a', icon: <CheckCircle size={12} /> },
    cancelled:  { bg: '#fee2e2', color: '#dc2626', icon: <XCircle size={12} /> },
    pending:    { bg: '#fef9c3', color: '#ca8a04', icon: <AlertCircle size={12} /> },
  };
  const { bg, color, icon } = cfg[status] || cfg.pending;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px',
      borderRadius:20, fontSize:'0.72rem', fontWeight:600, background:bg, color,
      textTransform:'capitalize', whiteSpace:'nowrap' }}>
      {icon} {status}
    </span>
  );
};

// ─── main component ───────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeNav, setActiveNav] = useState('dashboard');
  const [filter, setFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'date', dir: 'asc' });
  const [expandedRow, setExpandedRow] = useState(null);
  const [upcomingTab, setUpcomingTab] = useState('upcoming');
  const [gcalConnected, setGcalConnected] = useState(null); // null=loading, true/false
  const [gcalDisconnecting, setGcalDisconnecting] = useState(false);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/bookings`);
      setBookings(res.data);
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkGcal = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/google/status`);
      setGcalConnected(res.data.connected);
    } catch {
      setGcalConnected(false);
    }
  }, []);

  useEffect(() => { 
    fetchBookings(); 
    checkGcal(); 
  }, [fetchBookings, checkGcal]);

  const updateStatus = useCallback(async (id, status) => {
    try {
      await axios.put(`${API}/bookings/${id}/status`, { status });
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch {
      toast.error('Failed to update booking');
    }
  }, [fetchBookings]);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    window.location.reload();
  };

  const handleGcalConnect = () => {
    window.location.href = 'https://www.custodianai.co.nz/api/auth/google/authorize';
  };

  const handleGcalDisconnect = async () => {
    setGcalDisconnecting(true);
    try {
      await axios.delete(`${API}/auth/google/disconnect`);
      setGcalConnected(false);
      toast.success('Google Calendar disconnected');
    } catch {
      toast.error('Failed to disconnect');
    } finally {
      setGcalDisconnecting(false);
    }
  };

  // ── computed stats ──────────────────────────────────────────────────────────
  const today = todayStr();
  const [weekMon, weekSun] = weekRange();

  const stats = useMemo(() => {
    const todayCount   = bookings.filter(b => b.date === today).length;
    const weekCount    = bookings.filter(b => b.date >= weekMon && b.date <= weekSun).length;
    const pending      = bookings.filter(b => b.status === 'pending').length;
    const confirmed    = bookings.filter(b => b.status === 'confirmed').length;
    const cancelled    = bookings.filter(b => b.status === 'cancelled').length;
    return { todayCount, weekCount, pending, confirmed, cancelled, total: bookings.length };
  }, [bookings, today, weekMon, weekSun]);

  // ── upcoming / recently added ───────────────────────────────────────────────
  const upcoming = useMemo(() =>
    bookings
      .filter(b => b.date >= today && b.status !== 'cancelled')
      .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time))
      .slice(0, 5),
  [bookings, today]);

  const recentlyAdded = useMemo(() =>
    [...bookings].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5),
  [bookings]);

  // ── filtered / sorted table ─────────────────────────────────────────────────
  const tableData = useMemo(() => {
    const filtered = filter === 'all' ? bookings : bookings.filter(b => b.status === filter);
    return [...filtered].sort((a, b) => {
      const va = a[sortConfig.key] ?? '', vb = b[sortConfig.key] ?? '';
      const cmp = String(va).localeCompare(String(vb));
      return sortConfig.dir === 'asc' ? cmp : -cmp;
    });
  }, [bookings, filter, sortConfig]);

  const toggleSort = (key) =>
    setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));

  const renderSortIcon = (col) => {
    if (sortConfig.key !== col) {
      return <ChevronUp size={11} style={{ opacity:0.3 }} />;
    }
    if (sortConfig.dir === 'asc') {
      return <ChevronUp size={11} style={{ color:'#7c3aed' }} />;
    }
    return <ChevronDown size={11} style={{ color:'#7c3aed' }} />;
  };

  // ── nav items ────────────────────────────────────────────────────────────────
  const navItems = [
    { id: 'dashboard', label: 'Dashboard',    icon: <LayoutDashboard size={18} /> },
    { id: 'bookings',  label: 'All Bookings', icon: <ListChecks size={18} /> },
    { id: 'calendar',  label: 'Calendar',     icon: <Calendar size={18} /> },
    { id: 'settings',  label: 'Settings',     icon: <Settings size={18} /> },
  ];

  // ─── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="sb-layout">

      {/* ── Sidebar ── */}
      <aside className="sb-sidebar">
        <div className="sb-logo">
          <AnimatedLogo />
          <span className="sb-logo-text">Custodian AI</span>
        </div>

        <nav className="sb-nav">
          {navItems.map(({ id, label, icon }) => (
            <button
              key={id}
              className={`sb-nav-item ${activeNav === id ? 'sb-nav-active' : ''}`}
              onClick={() => setActiveNav(id)}
            >
              {icon}
              <span>{label}</span>
              {id === 'bookings' && stats.pending > 0 && (
                <span className="sb-nav-badge">{stats.pending}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="sb-sidebar-footer">
          <button className="sb-logout" onClick={handleLogout}>
            <LogOut size={16} /> <span>Log out</span>
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="sb-main">

        {/* top bar */}
        <div className="sb-topbar">
          <h1 className="sb-page-title">
            {activeNav === 'dashboard' && 'Dashboard'}
            {activeNav === 'bookings'  && 'All Bookings'}
            {activeNav === 'calendar'  && 'Calendar'}
            {activeNav === 'settings'  && 'Settings'}
          </h1>
          <div className="sb-topbar-right">
            <span className="sb-topbar-date">{new Date().toLocaleDateString('en-NZ', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</span>
            <button className="sb-refresh-btn" onClick={fetchBookings} title="Refresh">
              <RefreshCw size={15} />
            </button>
          </div>
        </div>

        {/* ══ DASHBOARD VIEW ══ */}
        {activeNav === 'dashboard' && (
          <div className="sb-content">

            {/* stat cards */}
            <div className="sb-stats-grid">
              {[
                { label: 'Today\'s Bookings', value: stats.todayCount, sub: new Date().toLocaleDateString('en-NZ', { weekday:'long', day:'numeric', month:'short' }), color: '#ef4444', iconBg: '#fff1f2', icon: <TrendingUp size={16} color="#ef4444" /> },
                { label: 'This Week', value: stats.weekCount, sub: `${fmtDate(weekMon)} – ${fmtDate(weekSun)}`, color: '#3b82f6', iconBg: '#eff6ff', icon: <Calendar size={16} color="#3b82f6" /> },
                { label: 'Pending', value: stats.pending, sub: 'Awaiting confirmation', color: '#f59e0b', iconBg: '#fffbeb', icon: <AlertCircle size={16} color="#f59e0b" /> },
                { label: 'Confirmed', value: stats.confirmed, sub: 'Ready to go', color: '#10b981', iconBg: '#f0fdf4', icon: <CheckCircle size={16} color="#10b981" /> },
                { label: 'Total Bookings', value: stats.total, sub: 'All time', color: '#8b5cf6', iconBg: '#f5f3ff', icon: <ListChecks size={16} color="#8b5cf6" /> },
                { label: 'Cancelled', value: stats.cancelled, sub: 'All time', color: '#ec4899', iconBg: '#fdf2f8', icon: <XCircle size={16} color="#ec4899" /> },
              ].map(({ label, value, sub, color, iconBg, icon }) => (
                <div key={label} className="sb-stat-card" style={{ '--sb-stat-accent': color }}>
                  <div className="sb-stat-header">
                    <div className="sb-stat-icon" style={{ background: iconBg }}>{icon}</div>
                    {label}
                  </div>
                  <div className="sb-stat-body">
                    <span className="sb-stat-num">{value}</span>
                  </div>
                  <div className="sb-stat-sub">{sub}</div>
                </div>
              ))}
            </div>

            {/* upcoming + recently added */}
            <div className="sb-upcoming-card">
              <div className="sb-upcoming-tabs">
                <button
                  className={`sb-tab ${upcomingTab === 'upcoming' ? 'sb-tab-active' : ''}`}
                  onClick={() => setUpcomingTab('upcoming')}
                >Upcoming Bookings</button>
                <button
                  className={`sb-tab ${upcomingTab === 'recent' ? 'sb-tab-active' : ''}`}
                  onClick={() => setUpcomingTab('recent')}
                >Recently Added</button>
              </div>

              {loading ? (
                <div className="sb-loading"><div className="sb-spinner" /></div>
              ) : (
                <table className="sb-upcoming-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Duration</th>
                      <th>Service</th>
                      <th>Client</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(upcomingTab === 'upcoming' ? upcoming : recentlyAdded).length === 0 ? (
                      <tr><td colSpan={6} className="sb-empty-row">No bookings to show</td></tr>
                    ) : (upcomingTab === 'upcoming' ? upcoming : recentlyAdded).map(b => (
                      <tr key={b.id} className="sb-upcoming-row">
                        <td>
                          <div className="sb-cell-date">{fmtDate(b.date)}</div>
                          <div className="sb-cell-sub">{b.time}</div>
                        </td>
                        <td><Clock size={13} style={{ marginRight:4, verticalAlign:'middle' }} />30 mins</td>
                        <td>
                          <div className="sb-cell-service">AI Automation Discovery Call</div>
                          {b.meet_link && (
                            <a href={b.meet_link} target="_blank" rel="noopener noreferrer" className="sb-meet-pill">
                              <Video size={11} /> Meet
                            </a>
                          )}
                        </td>
                        <td>
                          <div className="sb-cell-name">{b.name}</div>
                          <div className="sb-cell-sub">{b.company || b.email}</div>
                        </td>
                        <td><StatusBadge status={b.status} /></td>
                        <td>
                          <button className="sb-details-btn" onClick={() => { setActiveNav('bookings'); }}>
                            Details →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="sb-show-all">
                <button onClick={() => setActiveNav('bookings')} className="sb-show-all-btn">
                  Show all bookings →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ BOOKINGS TABLE VIEW ══ */}
        {activeNav === 'bookings' && (
          <div className="sb-content">
            {/* filter tabs */}
            <div className="sb-filter-bar">
              {['all','pending','confirmed','cancelled'].map(f => (
                <button
                  key={f}
                  className={`sb-filter-btn ${filter === f ? 'sb-filter-active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                  {f !== 'all' && stats[f] > 0 && <span className="sb-filter-count">{stats[f]}</span>}
                </button>
              ))}
              <span className="sb-filter-total">{tableData.length} booking{tableData.length !== 1 ? 's' : ''}</span>
            </div>

            <div className="sb-table-card">
              {loading ? (
                <div className="sb-loading"><div className="sb-spinner" /></div>
              ) : tableData.length === 0 ? (
                <div className="sb-empty-state">
                  <Calendar size={48} />
                  <p>No bookings found</p>
                </div>
              ) : (
                <table className="sb-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      {[['name','Client'],['date','Appointment'],['status','Status'],['company','Company'],['created_at','Submitted']].map(([key,label]) => (
                        <th key={key} className="sb-th-sort" onClick={() => toggleSort(key)}>
                          {label} {renderSortIcon(key)}
                        </th>
                      ))}
                      <th>Meet</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((b, idx) => (
                      <React.Fragment key={b.id}>
                        <tr
                          className={`sb-tr ${expandedRow === b.id ? 'sb-tr-expanded' : ''}`}
                          onClick={() => setExpandedRow(expandedRow === b.id ? null : b.id)}
                        >
                          <td className="sb-td-num">{idx + 1}</td>
                          <td>
                            <div className="sb-td-client">
                              <span className="sb-avatar">{(b.name || '?')[0].toUpperCase()}</span>
                              <div>
                                <div className="sb-cell-name">{b.name}</div>
                                <div className="sb-cell-sub">{b.email}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="sb-cell-date">{fmtDate(b.date)}</div>
                            <div className="sb-cell-sub">{b.time}</div>
                          </td>
                          <td><StatusBadge status={b.status} /></td>
                          <td>{b.company || <span className="sb-empty">—</span>}</td>
                          <td className="sb-cell-sub">{fmtDate(b.created_at)}</td>
                          <td onClick={e => e.stopPropagation()}>
                            {b.meet_link
                              ? <a href={b.meet_link} target="_blank" rel="noopener noreferrer" className="sb-meet-pill"><Video size={11} /> Join</a>
                              : <span className="sb-empty">—</span>
                            }
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            <div className="sb-actions">
                              {b.status === 'pending' && (
                                <>
                                  <button className="sb-act sb-act-confirm" onClick={() => updateStatus(b.id, 'confirmed')}>✓ Confirm</button>
                                  <button className="sb-act sb-act-cancel"  onClick={() => updateStatus(b.id, 'cancelled')}>✕ Cancel</button>
                                </>
                              )}
                              {b.status === 'confirmed' && (
                                <button className="sb-act sb-act-cancel" onClick={() => updateStatus(b.id, 'cancelled')}>✕ Cancel</button>
                              )}
                              {b.status === 'cancelled' && (
                                <button className="sb-act sb-act-confirm" onClick={() => updateStatus(b.id, 'confirmed')}>↺ Restore</button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {expandedRow === b.id && (
                          <tr className="sb-tr-detail">
                            <td colSpan={8}>
                              <div className="sb-detail-panel">
                                <div className="sb-detail-grid">
                                  {[
                                    [<User size={14}/>, 'Full Name', b.name],
                                    [<Mail size={14}/>, 'Email', b.email],
                                    [<Phone size={14}/>, 'Phone', b.phone || '—'],
                                    [<Building size={14}/>, 'Company', b.company || '—'],
                                    [<Calendar size={14}/>, 'Appointment', `${fmtDate(b.date)} at ${b.time}`],
                                  ].map(([icon, label, val]) => (
                                    <div key={label} className="sb-detail-item">
                                      {icon}
                                      <div>
                                        <div className="sb-detail-label">{label}</div>
                                        <div className="sb-detail-val">{val}</div>
                                      </div>
                                    </div>
                                  ))}
                                  {b.message && (
                                    <div className="sb-detail-item sb-detail-full">
                                      <MessageSquare size={14}/>
                                      <div>
                                        <div className="sb-detail-label">Message</div>
                                        <div className="sb-detail-val">{b.message}</div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ══ CALENDAR VIEW ══ */}
        {activeNav === 'calendar' && (
          <div className="sb-content sb-placeholder">
            <Calendar size={64} />
            <h2>Calendar View</h2>
            <p>Full calendar integration coming soon.</p>
          </div>
        )}

        {/* ══ SETTINGS VIEW ══ */}
        {activeNav === 'settings' && (
          <div className="sb-content">
            <div className="sb-settings-grid">

              {/* Google Calendar card */}
              <div className="sb-settings-card">
                <div className="sb-settings-card-header">
                  <div className="sb-settings-icon sb-settings-icon-gcal">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="4" width="18" height="17" rx="2" stroke="#1a73e8" strokeWidth="1.8" fill="white"/>
                      <path d="M3 9h18" stroke="#1a73e8" strokeWidth="1.8"/>
                      <path d="M8 2v4M16 2v4" stroke="#1a73e8" strokeWidth="1.8" strokeLinecap="round"/>
                      <rect x="7" y="13" width="4" height="4" rx="0.5" fill="#1a73e8"/>
                    </svg>
                  </div>
                  <div>
                    <div className="sb-settings-card-title">Google Calendar</div>
                    <div className="sb-settings-card-sub">hello@custodianai.co.nz</div>
                  </div>
                  {gcalConnected === null ? (
                    <span className="sb-gcal-status sb-gcal-loading">Checking…</span>
                  ) : gcalConnected ? (
                    <span className="sb-gcal-status sb-gcal-connected">● Connected</span>
                  ) : (
                    <span className="sb-gcal-status sb-gcal-disconnected">○ Not connected</span>
                  )}
                </div>

                <p className="sb-settings-card-desc">
                  When connected, every new booking automatically creates a Google Calendar event
                  with a Google Meet link — sent to both you and the client.
                </p>

                {gcalConnected ? (
                  <div className="sb-gcal-actions">
                    <div className="sb-gcal-info-row">
                      <CheckCircle size={15} style={{ color:'#16a34a' }} />
                      <span>New bookings will appear in your calendar automatically</span>
                    </div>
                    <button
                      className="sb-gcal-btn sb-gcal-btn-disconnect"
                      onClick={handleGcalDisconnect}
                      disabled={gcalDisconnecting}
                    >
                      {gcalDisconnecting ? 'Disconnecting…' : 'Disconnect Google Calendar'}
                    </button>
                  </div>
                ) : (
                  <button className="sb-gcal-btn sb-gcal-btn-connect" onClick={handleGcalConnect}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink:0 }}>
                      <rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M3 9h18M8 2v4M16 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Connect Google Calendar
                  </button>
                )}
              </div>

              {/* Account card */}
              <div className="sb-settings-card">
                <div className="sb-settings-card-header">
                  <div className="sb-settings-icon" style={{ background:'#f3f4f6' }}>
                    <User size={20} style={{ color:'#6b7280' }} />
                  </div>
                  <div>
                    <div className="sb-settings-card-title">Admin Account</div>
                    <div className="sb-settings-card-sub">Custodian AI</div>
                  </div>
                </div>
                <p className="sb-settings-card-desc">
                  Manage your admin credentials in the server environment variables
                  (<code>ADMIN_USERNAME</code> / <code>ADMIN_PASSWORD</code>).
                </p>
                <button className="sb-gcal-btn sb-gcal-btn-disconnect" onClick={handleLogout}>
                  <LogOut size={14} /> Sign out
                </button>
              </div>

            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default AdminDashboard;
